import React, { useEffect, useMemo, useRef, useState } from "react";
import { Filter, Search, X } from "lucide-react";

import styles from "./DoctorSearchInput.module.css";

/**
 * Doctor picker that's both a dropdown and a type-ahead search: focusing the
 * (empty) input opens every doctor as a scrollable list, same as a plain
 * select; typing narrows that same list down by name. Either way it filters
 * the appointments grid to the chosen doctor. The list is sourced from the
 * roster endpoint via useDoctors (/staffmember/getbyroster/true), passed in
 * as `doctors`.
 *
 * Single-select by nature: it answers "whose month is this", and a month grid
 * can only render one calendar. It holds that one id rather than a list, so it
 * cannot be handed a multi-selection it would silently reduce to its first
 * entry — which is what happened while it shared state with the Filters
 * dropdown.
 *
 * Clearing the input clears the pick. Keyboard support: ArrowUp/Down to move,
 * Enter to select, Escape to close.
 */
export const DoctorSearchInput = ({
  doctors,
  selectedDoctorId,
  onSelectDoctor,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Look up the active doctor object so we can render a chip showing
  // exactly who the appointments grid is filtered to.
  const selectedDoctor = useMemo(() => {
    if (!selectedDoctorId) return null;

    return (
      (doctors ?? []).find((d) => String(d.id) === String(selectedDoctorId)) ||
      null
    );
  }, [doctors, selectedDoctorId]);

  // If the pick is cleared from elsewhere, keep the input visually in sync.
  useEffect(() => {
    if (!selectedDoctorId) {
      setQuery("");
    }
  }, [selectedDoctorId]);

  // Close on click outside.
  useEffect(() => {
    const handler = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Empty query → every doctor, alphabetically (the dropdown behaviour).
  // Non-empty query → narrowed to the ones matching by name (the search
  // behaviour). Same list, same rendering below either way.
  const suggestions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const matches = trimmed
      ? (doctors ?? []).filter((d) =>
          `${d.firstName ?? ""} ${d.lastName ?? ""}`.toLowerCase().includes(trimmed)
        )
      : doctors ?? [];

    return [...matches].sort((a, b) =>
      `${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(
        `${b.firstName ?? ""} ${b.lastName ?? ""}`
      )
    );
  }, [doctors, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions]);

  const handleSelect = (doctor) => {
    if (!doctor) return;
    onSelectDoctor(doctor.id);
    setQuery(`${doctor.firstName ?? ""} ${doctor.lastName ?? ""}`.trim());
    setIsOpen(false);
  };

  // Clearing the box's text (not the filter itself) on focus is what makes
  // the dropdown show every doctor even when one is already selected —
  // otherwise the query would still hold their name and "filter" the list
  // down to just that one option, same as the search box always did.
  const handleFocus = () => {
    setIsOpen(true);
    if (selectedDoctor) setQuery("");
  };

  // Nothing new was picked before the box lost focus — put the current
  // selection's name back rather than leaving the box looking empty (and
  // therefore unfiltered) while a filter is still actually applied.
  const handleBlur = () => {
    if (selectedDoctor && query.trim() === "") {
      setQuery(`${selectedDoctor.firstName ?? ""} ${selectedDoctor.lastName ?? ""}`.trim());
    }
  };

  const handleClear = () => {
    setQuery("");
    onSelectDoctor(null);
    setIsOpen(false);
    // Drop straight back into a focused, empty input so the user can
    // immediately pick a different doctor.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      if (suggestions.length === 0) return;
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (suggestions.length === 0) return;
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      if (isOpen && suggestions[activeIndex]) {
        event.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Open on its own, with no query needed — that's what makes this a
  // dropdown rather than just a search box that happens to suggest matches.
  const showDropdown = isOpen;
  const isFiltered = !!selectedDoctor;

  return (
    <div
      className={`${styles.wrapper} ${
        isFiltered ? styles.wrapperFiltered : ""
      }`}
      ref={containerRef}
    >
      {isFiltered ? (
        <Filter size={16} className={`${styles.icon} ${styles.iconFiltered}`} />
      ) : (
        <Search size={16} className={styles.icon} />
      )}
      <input
        ref={inputRef}
        type="text"
        className={`${styles.input} ${
          isFiltered ? styles.inputFiltered : ""
        }`}
        placeholder="Search or select a staff member…"
        value={query}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          setIsOpen(true);
          // Emptying the text also turns the filter off — backspace
          // through the name is the natural way to clear.
          if (next.trim().length === 0 && isFiltered) {
            onSelectDoctor(null);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls="doctor-search-listbox"
        role="combobox"
      />
      {(query || isFiltered) && (
        <button
          type="button"
          aria-label="Clear doctor filter"
          className={styles.clearBtn}
          onClick={handleClear}
        >
          <X size={14} />
        </button>
      )}
      {showDropdown && (
        <ul
          id="doctor-search-listbox"
          className={styles.dropdown}
          role="listbox"
        >
          {suggestions.length === 0 ? (
            <li className={styles.empty}>
              {query.trim() ? "No matching doctors" : "No staff available"}
            </li>
          ) : (
            suggestions.map((d, i) => (
              <li
                key={d.id}
                role="option"
                aria-selected={i === activeIndex}
                className={`${styles.item} ${
                  i === activeIndex ? styles.itemActive : ""
                }`}
                onMouseDown={(e) => {
                  // mousedown fires before the input's blur, so the
                  // click registers even though focus is leaving.
                  e.preventDefault();
                  handleSelect(d);
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span>
                  {d.firstName} {d.lastName}
                </span>
                {d.staffRole && (
                  <span className={styles.role}>
                    ({d.staffRole.toLowerCase()})
                  </span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
